<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-3">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div class="flex items-center gap-4">
					<button
						type="button"
						class="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 border-none text-gray-700 cursor-pointer transition-colors hover:bg-gray-200"
						title="Back to Page Backgrounds"
						@click="goBack"
					>
						<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
						</svg>
					</button>
					<div>
						<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Page Background</h1>
						<p class="text-sm text-gray-500 mt-1 mb-0">ID: {{ routeId }}</p>
					</div>
				</div>
				<!-- Desktop Save Button -->
				<button
					type="button"
					class="save-btn-desktop inline-flex items-center gap-2 bg-orange-7 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
					:disabled="isSaving"
					@click="saveChanges"
				>
					<svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
						<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
						<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
					</svg>
					<svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
					</svg>
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</header>

		<!-- Main Content -->
		<main class="w-full p-3">
			<!-- Loading State -->
			<div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
				<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
					<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
					<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
				</svg>
				<span class="ml-3 text-gray-500 text-sm">Loading page background...</span>
			</div>

			<!-- Form -->
			<div v-else class="grid grid-cols-1 gap-4 edit-content-grid">
				<!-- Properties Card -->
				<div class="bg-white rounded-xl shadow-sm overflow-hidden">
					<!-- Card Header -->
					<div class="py-4 px-6 border-b border-gray-200 flex items-center gap-3">
						<svg class="w-5 h-5 text-orange-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
							<path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
						</svg>
						<h2 class="text-lg font-semibold text-gray-900 m-0">Properties</h2>
					</div>

					<!-- Form Fields -->
					<div class="p-6">
						<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
							<!-- Name -->
							<div>
								<label class="block text-sm font-medium text-gray-700 mb-2">
									Name
								</label>
								<input
									v-model="pageBackground.name"
									type="text"
									placeholder="Enter name"
									class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
								>
							</div>

							<!-- Color -->
							<div>
								<label class="block text-sm font-medium text-gray-700 mb-2">
									Color
								</label>
								<div class="flex gap-2 items-center">
									<input
										v-model="pageBackground.color"
										type="text"
										placeholder="e.g., #ffffff or rgba(255,255,255,1)"
										class="flex-1 py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
									>
									<div
										v-if="pageBackground.color"
										class="w-10 h-10 rounded-lg border border-gray-300 flex-shrink-0"
										:style="{ backgroundColor: pageBackground.color }"
									></div>
								</div>
							</div>

							<!-- Serial Number -->
							<div>
								<label class="block text-sm font-medium text-gray-700 mb-2">
									Serial Number
								</label>
								<input
									v-model.number="pageBackground.serialnumber"
									type="number"
									placeholder="Enter serial number"
									class="w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2 box-border"
								>
							</div>
						</div>
					</div>
				</div>

				<!-- Image Card -->
				<div class="bg-white rounded-xl shadow-sm overflow-hidden">
					<!-- Card Header -->
					<div class="py-4 px-6 border-b border-gray-200 flex items-center gap-3">
						<svg class="w-5 h-5 text-orange-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
						</svg>
						<h2 class="text-lg font-semibold text-gray-900 m-0">Background Image</h2>
					</div>

					<!-- Image Upload -->
					<div class="p-6">
						<div class="flex flex-col gap-4">
							<!-- Current Image Preview -->
							<div v-if="pageBackground.image" class="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
								<img
									:src="pageBackground.image"
									alt="Current background"
									class="max-w-[150px] max-h-[100px] rounded-md border border-gray-300 object-cover"
								>
								<div class="flex-1">
									<p class="text-sm text-gray-700 mb-2 break-all">{{ pageBackground.image }}</p>
									<button
										type="button"
										class="inline-flex items-center gap-1 py-1.5 px-3 bg-red-50 text-red-600 border border-red-200 rounded-md text-xs cursor-pointer transition-colors hover:bg-red-100"
										@click="removeImage"
									>
										<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
											<path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
										</svg>
										Remove
									</button>
								</div>
							</div>

							<!-- Upload Button -->
							<button
								type="button"
								class="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer transition-colors hover:border-orange-7 hover:bg-orange-1"
								@click="uploadImage"
							>
								<svg class="w-8 h-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
									<path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
								</svg>
								<span class="text-sm font-medium text-orange-7">{{ pageBackground.image ? 'Replace Image' : 'Upload Image' }}</span>
								<span class="text-xs text-gray-500 mt-1">Click to browse files</span>
							</button>
						</div>
					</div>
				</div>

				<!-- Mobile Save Button -->
				<div class="save-btn-mobile hidden">
					<button
						type="button"
						class="w-full inline-flex items-center justify-center gap-2 bg-orange-7 text-white py-3 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-70 disabled:cursor-not-allowed"
						:disabled="isSaving"
						@click="saveChanges"
					>
						<svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
						<svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
						</svg>
						{{ isSaving ? 'Saving...' : 'Save Changes' }}
					</button>
				</div>
			</div>
		</main>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Swal from 'sweetalert2';
import { PickerOptions, PickerResponse } from 'filestack-js';
import PageBackgroundService from 'services/PageBackgroundService';

export default defineComponent({
	name: 'EditPageBackgroundPage',
	setup() {
		const router = useRouter();
		const route = useRoute();
		const instance = getCurrentInstance();

		const routeId = computed(() => route.params.id as string);
		const pageBackground = ref<any>({});
		const isLoading = ref(true);
		const isSaving = ref(false);

		const goBack = () => {
			router.push('/pagebg');
		};

		const loadData = async () => {
			isLoading.value = true;
			try {
				const data = await PageBackgroundService.get(routeId.value);
				pageBackground.value = data;
			} catch (error: any) {
				console.error('Failed to load page background:', error);
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load page background',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				isLoading.value = false;
			}
		};

		const saveChanges = async () => {
			isSaving.value = true;
			try {
				await PageBackgroundService.update(routeId.value, { ...pageBackground.value });
				Swal.fire({
					toast: true,
					position: 'top-end',
					icon: 'success',
					title: 'Page background saved',
					showConfirmButton: false,
					timer: 2000,
					timerProgressBar: true,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save page background',
					icon: 'error',
					customClass: {
						popup: 'windmill-swal-popup',
						title: 'windmill-swal-title',
						htmlContainer: 'windmill-swal-content',
						confirmButton: 'windmill-swal-confirm',
					},
				});
			} finally {
				isSaving.value = false;
			}
		};

		const uploadImage = () => {
			const filestackClient = instance?.appContext.config.globalProperties.$filestack;
			if (!filestackClient) return;

			const options: PickerOptions = {
				accept: ['image/*'],
				maxFiles: 1,
				onUploadDone: (result: PickerResponse) => {
					if (result.filesUploaded.length > 0) {
						pageBackground.value.image = result.filesUploaded[0].url;
					}
				},
			};
			filestackClient.picker(options).open();
		};

		const removeImage = () => {
			pageBackground.value.image = '';
		};

		onMounted(() => {
			loadData();
		});

		return {
			routeId,
			pageBackground,
			isLoading,
			isSaving,
			goBack,
			saveChanges,
			uploadImage,
			removeImage,
		};
	},
});
</script>

<style scoped>
@media (min-width: 1024px) {
	.edit-content-grid {
		grid-template-columns: 1fr 1fr !important;
	}
}

@media (max-width: 640px) {
	.save-btn-desktop {
		display: none !important;
	}
	.save-btn-mobile {
		display: block !important;
	}
}
</style>
